import {useCallback, useEffect, useRef} from 'react';
import {markMessageRead} from '../../../service/ChatService';
import {isServerMessage} from '../serverMessages';

// Deduplicate each explicit click independently; message IDs are not a read cursor.
export default function useMessageRead(scope, conversationId, actorId, report, enabled = true) {
    const state = useRef(null);
    const reportRef = useRef(report);
    reportRef.current = report;
    useEffect(() => {
        if (!enabled) return undefined;
        const owner = {scope, controller: new AbortController(), pending: new Map(), completed: new Map()};
        state.current = owner;
        return () => {owner.controller.abort(); if (state.current === owner) state.current = null;};
    }, [scope, enabled]);

    return useCallback(message => {
        const owner = state.current;
        if (owner?.scope !== scope || !isServerMessage(message) || message.deleted_at
            || message.read === true || message.sender?.id === actorId || document.visibilityState !== 'visible') return Promise.resolve(null);
        if (owner.completed.has(message.id)) return Promise.resolve(owner.completed.get(message.id));
        if (owner.pending.has(message.id)) return owner.pending.get(message.id);
        const request = markMessageRead(message.id, owner.controller.signal).then(receipt => {
            if (owner.controller.signal.aborted || state.current !== owner) return null;
            owner.completed.set(message.id, receipt);
            window.dispatchEvent(new CustomEvent('chat-read-confirmed', {detail: {...receipt, actor_id: actorId, conversation_id: conversationId}}));
            return receipt;
        }).catch(error => {
            if (!owner.controller.signal.aborted && error?.name !== 'AbortError') reportRef.current(error);
            return null;
        }).finally(() => owner.pending.delete(message.id));
        owner.pending.set(message.id, request);
        return request;
    }, [scope, conversationId, actorId]);
}
